// utils/coursePreviewVideoApi.js
// The free preview video shown on a course's public website page. Shared by
// the batch and self-paced course editors, which both edit the same Course.
import apiClient from "./apiClient";

/** Mirrors the server's limits (vc_be utils/coursePreviewVideo.js). */
export const PREVIEW_VIDEO_MAX_MB = 500;
export const PREVIEW_VIDEO_ACCEPT = "video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v";
const ALLOWED_TYPES = new Set(["video/mp4", "video/webm", "video/quicktime", "video/x-m4v"]);
const ALLOWED_EXTENSIONS = new Set(["mp4", "webm", "mov", "m4v"]);

/**
 * Why this file cannot be uploaded, or null when it can. Checked before the
 * upload starts: a file over the limit is otherwise refused only after the
 * whole thing has been sent, and the browser may report that as a network
 * error rather than the server's message.
 * @param {File} file
 * @returns {string|null}
 */
export function previewVideoProblem(file) {
  if (!file) return "Choose a video file.";
  const extension = (file.name || "").split(".").pop().toLowerCase();
  if (!ALLOWED_TYPES.has(file.type) && !ALLOWED_EXTENSIONS.has(extension)) {
    return "Upload an MP4, WebM or MOV video.";
  }
  const sizeMb = file.size / (1024 * 1024);
  if (sizeMb > PREVIEW_VIDEO_MAX_MB) {
    return `This video is ${Math.round(sizeMb)} MB and the limit is ${PREVIEW_VIDEO_MAX_MB} MB. `
      + "Trim it, or export it at 1080p or lower, and try again.";
  }
  return null;
}

/**
 * Upload or replace a course's preview video. It is saved on the course the
 * moment the upload finishes — not held for the course form's Save button.
 * @param {string} courseId
 * @param {File} file
 * @param {{ onProgress?: (percent: number) => void, signal?: AbortSignal }} [opts]
 * @returns {Promise<object>} the saved preview, in the shape GET /courses/:id returns it
 */
export async function uploadCoursePreviewVideo(courseId, file, { onProgress, signal } = {}) {
  const body = new FormData();
  body.append("file", file);
  const { data } = await apiClient.post(`/courses/${courseId}/preview-video`, body, {
    headers: { "Content-Type": "multipart/form-data" },
    signal,
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.min(100, Math.round((e.loaded * 100) / e.total)));
    },
  });
  return data.previewVideo;
}

/** Take the preview video off the course and the website. */
export const removeCoursePreviewVideo = (courseId) =>
  apiClient.delete(`/courses/${courseId}/preview-video`);

/** The server's message for a failed upload, in words an admin can act on. */
export function previewVideoUploadError(err) {
  const status = err?.response?.status;
  if (err?.response?.data?.message) return err.response.data.message;
  if (status === 408 || err?.code === "ECONNABORTED") {
    return "The upload took too long. Try a shorter or smaller video.";
  }
  if (!err?.response) return "The upload was interrupted. Check your connection and try again.";
  return "Could not upload the preview video.";
}
